import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  FileText,
  PlusCircle,
  Download,
  Clock,
  CheckCircle2,
  XCircle,
  Upload,
  Search,
  Filter,
  Building,
  User,
  ShieldAlert,
} from "lucide-react";

const TIPOS_CONSTANCIA = [
  "Laboral Simple",
  "Laboral con Salario",
  "Dirigida a Banco / Entidad Financiera",
  "Dirigida a Embajada / Consulado",
  "Constancia con Horario y Funciones",
  "Otra",
];

const EstadoBadge = ({ estado }) => {
  const configs = {
    pendiente: { label: "Pendiente", className: "bg-amber-100 text-amber-800 border-amber-300", icon: Clock },
    emitida: { label: "Emitida", className: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: CheckCircle2 },
    rechazada: { label: "Rechazada", className: "bg-rose-100 text-rose-800 border-rose-300", icon: XCircle },
  };
  const config = configs[estado] || configs.pendiente;
  const Icon = config.icon;
  return (
    <Badge variant="outline" className={`flex items-center gap-1.5 px-2.5 py-0.5 font-medium ${config.className}`}>
      <Icon className="w-3.5 h-3.5" />
      <span>{config.label}</span>
    </Badge>
  );
};

export default function ConstanciasPage() {
  const { user } = useAuth();
  const isHRorAdmin = user?.rol === "administrador" || user?.rol === "rrhh";

  const [myConstancies, setMyConstancies] = useState([]);
  const [allConstancies, setAllConstancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterEstado, setFilterEstado] = useState("todos");

  // Modal Crear
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createData, setCreateData] = useState({
    tipo_constancia: "Laboral Simple",
    dirigida_a: "A Quien Corresponda",
    incluir_salario: false,
    motivo: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // Modal Responder (RRHH)
  const [isRespondOpen, setIsRespondOpen] = useState(false);
  const [selectedReq, setSelectedReq] = useState(null);
  const [respondAction, setRespondAction] = useState("emitir");
  const [respondComment, setRespondComment] = useState("");
  const [respondFile, setRespondFile] = useState(null);
  const [responding, setResponding] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const myRes = await api.getMisConstancias();
      setMyConstancies(myRes.data || []);

      if (isHRorAdmin) {
        const allRes = await api.getAllConstancias();
        setAllConstancies(allRes.data || []);
      }
    } catch (err) {
      toast.error("Error al cargar constancias");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [isHRorAdmin]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.createConstancia(createData);
      toast.success("Solicitud de constancia enviada exitosamente");
      setIsCreateOpen(false);
      setCreateData({
        tipo_constancia: "Laboral Simple",
        dirigida_a: "A Quien Corresponda",
        incluir_salario: false,
        motivo: "",
      });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al solicitar constancia");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async (id, nombreDoc = "Constancia_Laboral.pdf") => {
    try {
      const res = await api.downloadConstancia(id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", nombreDoc);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Descarga iniciada");
    } catch (err) {
      toast.error("Error al descargar el archivo de la constancia");
    }
  };

  const handleOpenRespond = (req) => {
    setSelectedReq(req);
    setRespondAction("emitir");
    setRespondComment("");
    setRespondFile(null);
    setIsRespondOpen(true);
  };

  const handleRespondSubmit = async (e) => {
    e.preventDefault();
    if (!selectedReq) return;
    setResponding(true);
    try {
      const formData = new FormData();
      formData.append("accion", respondAction);
      if (respondComment) formData.append("comentario", respondComment);
      if (respondFile) formData.append("archivo", respondFile);

      await api.responderConstancia(selectedReq.id, formData);
      toast.success(`Constancia ${respondAction === "emitir" ? "emitida" : "rechazada"} correctamente`);
      setIsRespondOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al procesar la constancia");
    } finally {
      setResponding(false);
    }
  };

  const filteredAllConstancies = allConstancies.filter((c) => {
    const matchesSearch =
      c.empleado_nombre?.toLowerCase().includes(search.toLowerCase()) ||
      c.empleado_dpi?.includes(search) ||
      c.tipo_constancia?.toLowerCase().includes(search.toLowerCase()) ||
      c.dirigida_a?.toLowerCase().includes(search.toLowerCase());
    const matchesEstado = filterEstado === "todos" || c.estado === filterEstado;
    return matchesSearch && matchesEstado;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2.5">
            <div className="p-2 bg-orange-100 rounded-lg text-orange-600">
              <FileText className="w-6 h-6" />
            </div>
            Constancias Laborales
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Solicitud, emisión oficial y descarga digital de constancias de trabajo
          </p>
        </div>
        <Button
          onClick={() => setIsCreateOpen(true)}
          className="bg-orange-600 hover:bg-orange-700 text-white shadow-sm flex items-center gap-2"
        >
          <PlusCircle className="w-4 h-4" />
          Nueva Solicitud
        </Button>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="mis-solicitudes" className="space-y-6">
        <TabsList className="bg-slate-100 p-1 rounded-xl">
          <TabsTrigger value="mis-solicitudes" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
            Mis Solicitudes ({myConstancies.length})
          </TabsTrigger>
          {isHRorAdmin && (
            <TabsTrigger value="gestion-rrhh" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
              Gestión RRHH ({allConstancies.filter((c) => c.estado === "pendiente").length} pendientes)
            </TabsTrigger>
          )}
        </TabsList>

        {/* Tab 1: Mis Solicitudes */}
        <TabsContent value="mis-solicitudes" className="space-y-4">
          {loading ? (
            <div className="text-center py-12 text-slate-500">Cargando solicitudes...</div>
          ) : myConstancies.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center space-y-3">
                <FileText className="w-12 h-12 text-slate-300 mx-auto" />
                <h3 className="font-semibold text-slate-700 text-lg">No tienes solicitudes de constancia</h3>
                <p className="text-sm text-slate-500 max-w-sm mx-auto">
                  Si necesitas una constancia laboral simple o con salario para trámite bancario o embajada, haz clic en el botón superior.
                </p>
                <Button onClick={() => setIsCreateOpen(true)} variant="outline" className="border-orange-200 text-orange-600 hover:bg-orange-50">
                  Solicitar Constancia
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {myConstancies.map((req) => (
                <Card key={req.id} className="border border-slate-200 hover:shadow-md transition-shadow duration-200">
                  <CardHeader className="pb-3 flex flex-row items-start justify-between space-y-0">
                    <div className="space-y-1">
                      <CardTitle className="text-base font-semibold text-slate-800">{req.tipo_constancia}</CardTitle>
                      <CardDescription className="text-xs text-slate-500">
                        Para: <span className="font-medium text-slate-700">{req.dirigida_a}</span>
                      </CardDescription>
                    </div>
                    <EstadoBadge estado={req.estado} />
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <div className="bg-slate-50 p-2.5 rounded-lg text-xs space-y-1.5 text-slate-600">
                      <div>
                        <span className="text-slate-400">Solicitado:</span> {req.fecha_solicitud || "-"}
                      </div>
                      {req.incluir_salario && (
                        <div className="text-emerald-700 font-medium">✓ Incluye información salarial</div>
                      )}
                      {req.motivo && (
                        <div>
                          <span className="text-slate-400">Motivo:</span> {req.motivo}
                        </div>
                      )}
                      {req.respuesta_rrhh && (
                        <div className="pt-1 border-t border-slate-200 text-slate-700">
                          <span className="font-semibold">Respuesta RRHH:</span> {req.respuesta_rrhh}
                        </div>
                      )}
                    </div>

                    {req.estado === "emitida" && (
                      <Button
                        onClick={() => handleDownload(req.id, `Constancia_${req.tipo_constancia.replace(/ /g, "_")}.pdf`)}
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white flex items-center justify-center gap-2"
                        size="sm"
                      >
                        <Download className="w-4 h-4" />
                        Descargar Constancia Oficial
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Tab 2: Gestión RRHH */}
        {isHRorAdmin && (
          <TabsContent value="gestion-rrhh" className="space-y-4">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 items-center justify-between bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
              <div className="relative w-full sm:w-80">
                <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                <Input
                  placeholder="Buscar por colaborador, DPI..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <Filter className="w-4 h-4 text-slate-400" />
                <Select value={filterEstado} onValueChange={setFilterEstado}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos los Estados</SelectItem>
                    <SelectItem value="pendiente">Solo Pendientes</SelectItem>
                    <SelectItem value="emitida">Solo Emitidas</SelectItem>
                    <SelectItem value="rechazada">Solo Rechazadas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-slate-50 text-slate-600 border-b border-slate-200 font-medium">
                    <tr>
                      <th className="px-4 py-3">Colaborador</th>
                      <th className="px-4 py-3">Tipo y Destinatario</th>
                      <th className="px-4 py-3">Salario</th>
                      <th className="px-4 py-3">Fecha Solicitud</th>
                      <th className="px-4 py-3">Estado</th>
                      <th className="px-4 py-3 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredAllConstancies.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-8 text-slate-500">
                          No se encontraron solicitudes con los filtros actuales
                        </td>
                      </tr>
                    ) : (
                      filteredAllConstancies.map((req) => (
                        <tr key={req.id} className="hover:bg-slate-50/80 transition-colors">
                          <td className="px-4 py-3">
                            <div className="font-semibold text-slate-800">{req.empleado_nombre}</div>
                            <div className="text-xs text-slate-400">
                              DPI: {req.empleado_dpi} | {req.puesto} ({req.departamento})
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-medium text-slate-700">{req.tipo_constancia}</div>
                            <div className="text-xs text-slate-500">Dirigida a: {req.dirigida_a}</div>
                          </td>
                          <td className="px-4 py-3">
                            {req.incluir_salario ? (
                              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">Sí incluye</Badge>
                            ) : (
                              <span className="text-slate-400 text-xs">No</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-500">
                            {req.fecha_solicitud || "-"}
                          </td>
                          <td className="px-4 py-3">
                            <EstadoBadge estado={req.estado} />
                          </td>
                          <td className="px-4 py-3 text-right">
                            {req.estado === "pendiente" ? (
                              <Button
                                size="sm"
                                onClick={() => handleOpenRespond(req)}
                                className="bg-orange-600 hover:bg-orange-700 text-white"
                              >
                                Responder
                              </Button>
                            ) : req.estado === "emitida" ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDownload(req.id, `Constancia_${req.empleado_nombre}.pdf`)}
                                className="border-slate-200 hover:bg-slate-100"
                              >
                                <Download className="w-4 h-4 mr-1 text-slate-600" />
                                Ver PDF
                              </Button>
                            ) : (
                              <span className="text-xs text-slate-400">Atendida</span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* Modal: Solicitar Constancia */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-orange-600" />
              Solicitud de Constancia Laboral
            </DialogTitle>
            <DialogDescription>
              Completa los detalles para que el departamento de RRHH emita tu documento oficial.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreate} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="tipo">Tipo de Constancia *</Label>
              <Select
                value={createData.tipo_constancia}
                onValueChange={(val) => setCreateData({ ...createData, tipo_constancia: val })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_CONSTANCIA.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="dirigida_a">¿A quién va dirigida? *</Label>
              <Input
                id="dirigida_a"
                placeholder="Ej. A Quien Corresponda, Banco Industrial, Embajada..."
                value={createData.dirigida_a}
                onChange={(e) => setCreateData({ ...createData, dirigida_a: e.target.value })}
                required
              />
            </div>

            <div className="flex items-center space-x-2 pt-1">
              <Checkbox
                id="incluir_salario"
                checked={createData.incluir_salario}
                onCheckedChange={(checked) => setCreateData({ ...createData, incluir_salario: !!checked })}
              />
              <Label htmlFor="incluir_salario" className="text-sm font-normal cursor-pointer">
                Requiero que la constancia incluya información de ingresos/salario
              </Label>
            </div>

            <div className="space-y-2">
              <Label htmlFor="motivo">Motivo o detalles adicionales (Opcional)</Label>
              <Textarea
                id="motivo"
                placeholder="Indica cualquier especificación necesaria para RRHH..."
                value={createData.motivo}
                onChange={(e) => setCreateData({ ...createData, motivo: e.target.value })}
                rows={3}
              />
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting} className="bg-orange-600 hover:bg-orange-700 text-white">
                {submitting ? "Enviando..." : "Enviar Solicitud"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal: Responder Solicitud (RRHH) */}
      <Dialog open={isRespondOpen} onOpenChange={setIsRespondOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Procesar Solicitud de Constancia</DialogTitle>
            <DialogDescription>
              {selectedReq && (
                <span>
                  Colaborador: <strong>{selectedReq.empleado_nombre}</strong> ({selectedReq.puesto})
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleRespondSubmit} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Acción a Realizar *</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant={respondAction === "emitir" ? "default" : "outline"}
                  onClick={() => setRespondAction("emitir")}
                  className={respondAction === "emitir" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}
                >
                  <CheckCircle2 className="w-4 h-4 mr-1.5" />
                  Emitir y Aprobar
                </Button>
                <Button
                  type="button"
                  variant={respondAction === "rechazar" ? "destructive" : "outline"}
                  onClick={() => setRespondAction("rechazar")}
                >
                  <XCircle className="w-4 h-4 mr-1.5" />
                  Rechazar
                </Button>
              </div>
            </div>

            {respondAction === "emitir" && (
              <div className="space-y-2 bg-slate-50 p-3 rounded-lg border border-slate-200">
                <Label htmlFor="custom_pdf" className="text-xs font-semibold text-slate-700 block">
                  Archivo PDF de la Constancia (Opcional):
                </Label>
                <Input
                  id="custom_pdf"
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setRespondFile(e.target.files?.[0] || null)}
                  className="bg-white text-xs"
                />
                <p className="text-xs text-slate-500">
                  * Si no adjuntas un archivo, el sistema <strong>generará automáticamente</strong> la constancia oficial
                  en PDF con el membrete y datos de la empresa.
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="respondComment">Comentario / Observaciones</Label>
              <Textarea
                id="respondComment"
                placeholder="Mensaje para el colaborador..."
                value={respondComment}
                onChange={(e) => setRespondComment(e.target.value)}
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsRespondOpen(false)}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={responding}
                className={respondAction === "emitir" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-rose-600 hover:bg-rose-700 text-white"}
              >
                {responding ? "Procesando..." : respondAction === "emitir" ? "Confirmar Emisión" : "Confirmar Rechazo"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
